import React from 'react';
import type { PcV2Channel } from '../../../patient-comms/typesV2';
import {
  PC_V2_CHANNELS,
  PC_V2_CHANNEL_ORDER,
  PC_V2_CHANNEL_PLURAL,
} from '../../../patient-comms/utilsV2';
import { Icon } from '../../Icon/Icon';

export interface PatientCommsChannelTilesProps {
  counts: Record<PcV2Channel, number>;
  /** null = no filter, show everything. */
  active: PcV2Channel | null;
  onChange: (channel: PcV2Channel | null) => void;
}

/**
 * Three tiles: how much of each channel, and the filter for it.
 *
 * The tile is the filter — a separate row of filter pills would say the same
 * thing twice. Clicking the active tile clears the filter, so there is no
 * fourth "All" tile competing with the three real ones.
 *
 * No sub-labels ("8 read · 5 replied"): those were delivery-status tags, and
 * the tab does not show delivery status. A count is a fact; a read receipt on
 * a text we cannot verify is not.
 */
export function PatientCommsChannelTiles({ counts, active, onChange }: PatientCommsChannelTilesProps) {
  return (
    <div className="mb-5 grid gap-2.5" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
      {PC_V2_CHANNEL_ORDER.map((channel) => {
        const ch = PC_V2_CHANNELS[channel];
        const on = active === channel;
        const count = counts[channel];
        return (
          <button
            key={channel}
            type="button"
            aria-pressed={on}
            onClick={() => {
              onChange(on ? null : channel);
            }}
            className="cursor-pointer rounded-[var(--r-md)] border bg-[var(--cream-1)] px-3.5 pb-3 pt-3.5 text-left font-[var(--font-body)] transition-colors"
            style={{
              borderColor: on ? ch.color : 'var(--cream-2)',
              boxShadow: on ? `0 0 0 1px ${ch.color}` : 'none',
              opacity: count === 0 && !on ? 0.55 : 1,
            }}
          >
            <div className="mb-2.5 flex items-center gap-[7px]">
              <span
                className="flex h-[22px] w-[22px] items-center justify-center rounded-md"
                style={{ background: `color-mix(in srgb, ${ch.color} 12%, var(--cream-1))`, color: ch.color }}
              >
                <Icon kind={ch.icon} size={12} stroke={1.8} />
              </span>
              <span className="text-[11.5px] font-medium text-[var(--ink-3)]">
                {PC_V2_CHANNEL_PLURAL[channel]}
              </span>
            </div>
            <div className="font-[var(--font-mono)] text-[23px] font-semibold leading-none tracking-[-0.02em] text-[var(--ink-0)]">
              {count}
            </div>
          </button>
        );
      })}
    </div>
  );
}
